import { useEffect, useMemo, useState } from "react";
import Modal from "../common/Modal";
import Button from "../common/Button";
import { previewRainOut, recordRainOut } from "../../api/quotes";
import { formatUSD } from "../../utils/money";

// Weather stopped the event. We keep $250 for driving out and $250 for setup
// (only if they happened) plus the hours played; the rest of what the customer
// paid becomes a credit they can put toward a future booking. The math comes
// from the server so the preview is exactly what gets saved.

function bookedHours(job) {
  const s = new Date(job.event_date).getTime();
  const e = new Date(job.event_end).getTime();
  return s && e && e > s ? (e - s) / 3600000 : 4;
}

export default function RainOutModal({ job, onClose, onDone }) {
  const hours = bookedHours(job);
  const options = useMemo(() => {
    const out = [];
    for (let h = 0; h <= hours; h += 0.5) out.push(h);
    return out;
  }, [hours]);

  const [form, setForm] = useState({ hours_played: 0, drove_out: true, set_up: true });
  const [calc, setCalc] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    previewRainOut(job.id, form)
      .then((c) => active && setCalc(c))
      .catch((e) => active && setError(e.message || "Could not work out the credit."));
    return () => {
      active = false;
    };
  }, [job.id, form]);

  async function save() {
    setSaving(true);
    setError("");
    try {
      await recordRainOut(job.id, form);
      onDone();
    } catch (e) {
      setError(e.message || "Could not save the rain-out.");
      setSaving(false);
    }
  }

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  return (
    <Modal isOpen title="Rained out" onClose={onClose} maxWidthClass="max-w-md">
      <div className="space-y-4">
        <div className="text-sm text-birdie-charcoal/70">
          {job.customer_name} · {job.quote_number} · {hours}h booked
        </div>

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>
        )}

        <label className="block">
          <span className="block text-xs font-medium text-birdie-charcoal/70 mb-1">
            How long did they play before it stopped?
          </span>
          <select
            value={form.hours_played}
            onChange={(e) => set({ hours_played: Number(e.target.value) })}
            className="w-full px-3 py-2.5 text-sm border border-birdie-green/25 rounded-lg bg-white"
          >
            {options.map((h) => (
              <option key={h} value={h}>
                {h === 0 ? "Didn't start" : `${h} hour${h === 1 ? "" : "s"}`}
              </option>
            ))}
          </select>
        </label>

        <div className="space-y-2">
          <Check checked={form.drove_out} onChange={(v) => set({ drove_out: v })}>
            We drove out <span className="text-birdie-charcoal/50">(keep $250)</span>
          </Check>
          <Check checked={form.set_up} onChange={(v) => set({ set_up: v })}>
            We set up <span className="text-birdie-charcoal/50">(keep $250)</span>
          </Check>
        </div>

        {calc && (
          <table className="w-full text-sm">
            <tbody>
              <Line label="They've paid" cents={calc.paid_cents} />
              {calc.kept_fees_cents > 0 && <Line label="Travel + setup" cents={-calc.kept_fees_cents} />}
              {calc.kept_time_cents > 0 && (
                <Line
                  label={`${calc.hours_played}h played × ${formatUSD(calc.hourly_cents)}/h`}
                  cents={-calc.kept_time_cents}
                />
              )}
              <tr className="border-t border-birdie-green/15">
                <td className="pt-2 font-semibold text-birdie-charcoal">Credit for a future booking</td>
                <td className="pt-2 text-right font-semibold text-birdie-green tabular-nums">
                  {formatUSD(calc.credit_cents)}
                </td>
              </tr>
            </tbody>
          </table>
        )}

        <p className="text-xs text-birdie-charcoal/55">
          Any unpaid balance is cancelled. The credit shows up automatically next time you quote{" "}
          {job.customer_name?.split(" ")[0] || "them"}.
        </p>

        <Button onClick={save} disabled={saving || !calc} className="w-full py-3">
          {saving ? "Saving…" : "Save rain-out"}
        </Button>
      </div>
    </Modal>
  );
}

function Check({ checked, onChange, children }) {
  return (
    <label className="flex items-center gap-2 text-sm text-birdie-charcoal">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="rounded border-birdie-green/30"
      />
      <span>{children}</span>
    </label>
  );
}

function Line({ label, cents }) {
  return (
    <tr>
      <td className="py-0.5 text-birdie-charcoal/70">{label}</td>
      <td className="py-0.5 text-right tabular-nums text-birdie-charcoal/80">
        {cents < 0 ? `-${formatUSD(-cents)}` : formatUSD(cents)}
      </td>
    </tr>
  );
}
